import { useCallback, useEffect, useState } from 'react';

import { alterarCampo, definirMaterialChave, listarCampos, listarMateriaisChave } from '../../conteudo/api/conteudo';
import type { CampoConteudo, CamposDaFase, MateriaisChave } from '../../conteudo/api/conteudo';
import { alterarRegra, listarRegras } from '../../pontuacao/api/pontuacao';
import type { RegrasDaFase, ValorDaRegra } from '../../pontuacao/api/pontuacao';
import { CampoDoConteudo, chaveDoCampo, mensagemDaFalha, SETA, valorTipado } from './comum';
import type { Aviso } from './comum';

/**
 * A aba Modo Versus de Configurações, no padrão do Scan Pixel e do Laboratório: seletor de rodada
 * (o servidor não deixa criar rodadas), a rodada marcada com o case e o material-chave, os critérios
 * de avaliação e a Pontuação. Tudo fica pendente até "Salvar Alterações".
 */
export function VersusConfiguracao({ csrfToken }: { csrfToken: string }) {
    const [dados, setDados] = useState<CamposDaFase | null>(null);
    const [chave, setChave] = useState<MateriaisChave | null>(null);
    const [regras, setRegras] = useState<RegrasDaFase | null>(null);
    const [falhouCarregar, setFalhouCarregar] = useState(false);

    const [rascunho, setRascunho] = useState<Record<string, string>>({});
    const [materialRascunho, setMaterialRascunho] = useState<Record<string, string>>({});
    const [regrasRascunho, setRegrasRascunho] = useState<Record<string, string>>({});
    const [selecionada, setSelecionada] = useState(1);
    const [criteriosAbertos, setCriteriosAbertos] = useState(true);
    const [pontuacaoAberta, setPontuacaoAberta] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [aviso, setAviso] = useState<Aviso | null>(null);

    const carregar = useCallback(async () => {
        try {
            const [conteudo, materiais, todasAsRegras] = await Promise.all([listarCampos('versus'), listarMateriaisChave(), listarRegras()]);
            setDados(conteudo);
            setChave(materiais);
            setRegras(todasAsRegras.find((fase) => fase.fase === 'versus') ?? null);
            setFalhouCarregar(false);
        } catch {
            setFalhouCarregar(true);
        }
    }, []);

    useEffect(() => {
        void carregar();
    }, [carregar]);

    function descartar() {
        setRascunho({});
        setMaterialRascunho({});
        setRegrasRascunho({});
    }

    if (falhouCarregar) {
        return (
            <p className="estado-variaveis mensagem-login">
                Não foi possível carregar o Modo Versus.{' '}
                <button className="botao-texto" type="button" onClick={() => void carregar()}>Tentar novamente</button>
            </p>
        );
    }
    if (!dados || !chave || !regras) return <p className="estado-variaveis">Carregando o Modo Versus…</p>;

    const travado = !dados.editavel;
    const abertas = [...new Set([...dados.turmas_com_fase_aberta, ...chave.turmas_com_fase_aberta, ...regras.turmas_com_fase_aberta])];
    const doLocal = (local: string) => dados.campos.filter((campo) => campo.local === local);
    const rodadas = [...new Set(dados.campos.filter((campo) => campo.local.startsWith('Rodada ') && !campo.local.includes('›')).map((campo) => campo.local))]
        .sort((a, b) => Number(a.replace('Rodada ', '')) - Number(b.replace('Rodada ', '')));
    const criterios = [...new Set(dados.campos.filter((campo) => campo.local.startsWith('Critério ')).map((campo) => campo.local))]
        .sort((a, b) => Number(a.replace('Critério ', '')) - Number(b.replace('Critério ', '')));
    const marcada = selecionada >= 1 && selecionada <= rodadas.length ? selecionada : 1;
    const localMarcado = rodadas[marcada - 1];

    const valorDoCampo = (campo: CampoConteudo) => rascunho[chaveDoCampo(campo)] ?? (campo.valor === null ? '' : String(campo.valor));
    const desenharCampo = (campo: CampoConteudo) => (
        <CampoDoConteudo
            key={chaveDoCampo(campo)}
            campo={campo}
            valor={valorDoCampo(campo)}
            travado={travado}
            aoMudar={(valor) => setRascunho((atual) => ({ ...atual, [chaveDoCampo(campo)]: valor }))}
        />
    );
    const materialDaRodada = (atividadeId: string, original: string | null) => materialRascunho[atividadeId] ?? (original ?? '');
    const valorDaRegra = (chaveDaRegra: string) => regrasRascunho[chaveDaRegra] ?? String(regras.regras[chaveDaRegra] ?? '');

    async function salvar() {
        if (!dados || !chave || !regras) return;
        setAviso(null);
        setSalvando(true);
        try {
            for (const campo of dados.campos) {
                const digitado = rascunho[chaveDoCampo(campo)];
                const original = campo.valor === null ? '' : String(campo.valor);
                if (digitado === undefined || digitado === original) continue;
                await alterarCampo(campo, valorTipado(campo, digitado), campo.valor, csrfToken);
            }
            for (const rodada of chave.rodadas) {
                const escolhido = materialRascunho[rodada.atividade_id];
                if (escolhido === undefined || escolhido === (rodada.material_id ?? '')) continue;
                await definirMaterialChave(rodada.atividade_id, escolhido === '' ? null : escolhido, csrfToken);
            }
            for (const [chaveDaRegra, digitado] of Object.entries(regrasRascunho)) {
                if (digitado === String(regras.regras[chaveDaRegra] ?? '')) continue;
                await alterarRegra('versus', chaveDaRegra, digitado as ValorDaRegra, csrfToken);
            }
            descartar();
            setAviso({ tipo: 'sucesso', texto: 'Alterações salvas.' });
        } catch (erro) {
            setAviso({ tipo: 'erro', texto: mensagemDaFalha(erro) });
        } finally {
            setSalvando(false);
            await carregar();
        }
    }

    const rodadaDaChave = chave.rodadas.find((rodada) => rodada.local === localMarcado);

    return (
        <div className="config-fase">
            {abertas.length > 0 && (
                <p className="aviso-fase-aberta" role="status">
                    A fase Modo Versus está aberta na turma {abertas.join(', ')}: só leitura até ela fechar.
                </p>
            )}

            <div className="seletor-enigma-config">
                <label>
                    <span>Rodada:</span>
                    <select aria-label="Rodada" value={String(marcada)} onChange={(evento) => setSelecionada(Number(evento.target.value))}>
                        {rodadas.map((_, i) => (
                            <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                        ))}
                    </select>
                </label>
            </div>

            {localMarcado && (
                <section className="cartao-config" aria-labelledby="versus-rodada">
                    <div className="cabecalho-cartao-config">
                        <h3 id="versus-rodada">{localMarcado}</h3>
                    </div>
                    {doLocal(localMarcado).map(desenharCampo)}
                    {rodadaDaChave && (
                        <label className="campo-config">
                            Material-chave
                            <select
                                value={materialDaRodada(rodadaDaChave.atividade_id, rodadaDaChave.material_id)}
                                disabled={travado || !chave.editavel}
                                onChange={(evento) => setMaterialRascunho((atual) => ({ ...atual, [rodadaDaChave.atividade_id]: evento.target.value }))}
                            >
                                <option value="">Nenhum (chat livre)</option>
                                {chave.materiais.map((material) => (
                                    <option key={material.id} value={material.id}>{material.titulo}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    {rodadaDaChave && (
                        <p className="nota-config">O material-chave é o da biblioteca que libera a resposta do chat nesta rodada.</p>
                    )}
                    <section className="cartao-pista" aria-labelledby="versus-case">
                        <div className="cabecalho-cartao-config">
                            <h4 id="versus-case">Case</h4>
                        </div>
                        {doLocal(`${localMarcado} › Case`).map(desenharCampo)}
                    </section>
                </section>
            )}

            <section className="cartao-config" aria-labelledby="versus-criterios">
                <div className="cabecalho-cartao-config">
                    <h3 id="versus-criterios">Critérios de avaliação</h3>
                    <button
                        type="button"
                        className={`seta-config${criteriosAbertos ? ' seta-config-aberta' : ''}`}
                        aria-label={criteriosAbertos ? 'Recolher Critérios de avaliação' : 'Abrir Critérios de avaliação'}
                        aria-expanded={criteriosAbertos}
                        onClick={() => setCriteriosAbertos((aberto) => !aberto)}
                    >
                        {SETA}
                    </button>
                </div>
                {criteriosAbertos && criterios.map((local, indice) => (
                    <section key={local} className="cartao-pista" aria-labelledby={`versus-criterio-${indice + 1}`}>
                        <div className="cabecalho-cartao-config">
                            <h4 id={`versus-criterio-${indice + 1}`}>{local}</h4>
                        </div>
                        {doLocal(local).map(desenharCampo)}
                    </section>
                ))}
            </section>

            <section className="cartao-config" aria-labelledby="versus-pontuacao">
                <div className="cabecalho-cartao-config">
                    <h3 id="versus-pontuacao">Pontuação</h3>
                    <button
                        type="button"
                        className={`seta-config${pontuacaoAberta ? ' seta-config-aberta' : ''}`}
                        aria-label={pontuacaoAberta ? 'Recolher Pontuação' : 'Abrir Pontuação'}
                        aria-expanded={pontuacaoAberta}
                        onClick={() => setPontuacaoAberta((aberta) => !aberta)}
                    >
                        {SETA}
                    </button>
                </div>
                {pontuacaoAberta && (
                    <div className="grade-pontuacao">
                        <label className="campo-config">
                            Tipo do Ranking
                            <select
                                value={valorDaRegra('destino')}
                                disabled={!regras.editavel}
                                onChange={(evento) => setRegrasRascunho((atual) => ({ ...atual, destino: evento.target.value }))}
                            >
                                <option value="grupo">Grupos</option>
                                <option value="individual">Individual</option>
                            </select>
                        </label>
                        <p className="nota-config">As notas vêm da cédula do facilitador, pelos critérios acima.</p>
                    </div>
                )}
            </section>

            {aviso && (
                <p className={aviso.tipo === 'erro' ? 'mensagem-login aviso-cadastro' : 'mensagem-salvo'} role={aviso.tipo === 'erro' ? 'alert' : 'status'}>
                    {aviso.texto}
                </p>
            )}

            <div className="rodape-config">
                <button type="button" className="botao-cancelar-config" onClick={descartar} disabled={salvando}>
                    Cancelar
                </button>
                <button type="button" className="botao-salvar-config" onClick={() => void salvar()} disabled={salvando || travado}>
                    {salvando ? 'Salvando…' : 'Salvar Alterações'}
                </button>
            </div>
        </div>
    );
}
